import {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useLocation, useNavigate} from 'react-router-dom';
import {fetchHrCustomer} from '../utils/api';
import ErrorPanel from '../components/ErrorPanel';
import Header from '../components/Header';
import Loading from '../components/Loading';
import {useTranslation} from 'react-i18next';
import Support from '../components/dashboard/Support';
import ChatBot from '../components/Chatbot';
import HeroArea from '../components/dashboard/HeroArea';
import SectionProjects from '../components/dashboard/SectionProjects';

const Dashboard = () => {
  //params
  const location = useLocation();
  const state = location.state as {
    jwtToken: string;
  };

  const jwtToken = state?.jwtToken;

  const {t} = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [dashboardStatus, setDashboardStatus] = useState<
    'loading' | 'loaded' | 'error' | undefined
  >('loading');

  //project details
  const [customer, setCustomer] = useState<HRCustomer>();

  let navigate = useNavigate();
  let replace = useNavigate();

  useEffect(() => {
    const getData = async (jwtToken: string) => {
      try {
        const customerData = await fetchHrCustomer(jwtToken);

        if (
          customerData.projects.length === 1 &&
          !customerData.hasSharedOProjects
        ) {
          navigate(
            `/area-riservata/progetto/${customerData.projects[0].token}`,
            {
              state: {
                jwtToken,
                hideBack: true,
              },
            }
          );
        }

        if (
          customerData.projects.length <= 0 &&
          customerData.hasSharedOProjects
        ) {
          navigate(`/area-riservata/giocate-condivise/`, {
            state: {
              jwtToken,
              customer: customerData,
            },
          });
        }

        setCustomer(customerData);
        setDashboardStatus('loaded');
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          setErrorMessage(t(err.response.data.message));
        } else if (axios.isAxiosError(err) && err.response?.status === 401) {
          setErrorMessage(t('errors.link-not-valid'));
        } else {
          setErrorMessage(t('errors.generic-error'));
        }
        setDashboardStatus('error');
      }
    };

    if (jwtToken) {
      getData(jwtToken);
    } else {
      replace(`/area-riservata`);
    }
  }, [jwtToken, t, navigate, replace]);

  const handleProjectClick = useCallback(
    (token: string) => {
      navigate(`/area-riservata/progetto/${token}`, {
        state: {
          jwtToken,
        },
      });
    },
    [jwtToken, navigate]
  );

  const handleAllLinkClick = useCallback(() => {
    navigate(`/area-riservata/giocate-condivise/`, {
      state: {
        jwtToken,
        customer,
      },
    });
  }, [jwtToken, navigate, customer]);

  const renderMain = () => {
    if (dashboardStatus === 'loading') {
      return <Loading />;
    }

    if (dashboardStatus === 'error') {
      return <ErrorPanel errorMessage={errorMessage} />;
    }

    if (
      dashboardStatus === 'loaded' &&
      customer &&
      !customer.hasSharedOProjects &&
      customer.projects.length <= 0
    ) {
      return <ErrorPanel errorMessage={t('dashboard.config-missing')} />;
    }

    if (dashboardStatus === 'loaded') {
      return <Page />;
    }

    return <h1 style={{textAlign: 'center'}}>{t('errors.generic-error')}</h1>;
  };

  const Page = () => {
    return (
      <>
        <HeroArea
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
          refName={customer?.name}
          allText={t('dashboard.link-to-all-experiences')}
          onAllClick={
            customer?.hasSharedOProjects ? handleAllLinkClick : undefined
          }
        />

        <SectionProjects
          list={customer?.projects || []}
          onClick={handleProjectClick}
        />
      </>
    );
  };

  return (
    <>
      <Header clientLogo={customer?.logo} />
      <div style={{flex: '1'}}>{renderMain()}</div>
      <Support />
      <ChatBot />
    </>
  );
};

export default Dashboard;
